import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCompanyById, getReviewsByTarget } from '../services/api';
import Loading from '../components/Loading';

const CompanyProfilePage = () => {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCompanyData = async () => {
      setLoading(true);

      // Load company details
      const companyResult = await getCompanyById(companyId);
      if (companyResult.success && companyResult.company) {
        setCompany(companyResult.company);
      }

      // Load reviews
      const reviewsResult = await getReviewsByTarget(companyId);
      if (reviewsResult.success) {
        setReviews(reviewsResult.reviews);
      }

      setLoading(false);
    };

    if (companyId) {
      loadCompanyData();
    }
  }, [companyId]);

  if (loading) {
    return <Loading message="Loading company profile..." />;
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Company Not Found</h2>
          <Link to="/dashboard" className="text-blue-600 hover:underline">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 'N/A';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Company Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-start space-x-6">
            <img
              src={company.logo}
              alt={company.name}
              className="w-32 h-32 rounded-lg object-cover border-2 border-gray-200"
            />
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                {company.name}
              </h1>
              <div className="flex items-center space-x-4 text-gray-600 mb-4">
                <span className="flex items-center">
                  <span className="text-yellow-500 mr-1">★</span>
                  {averageRating} ({reviews.length} reviews)
                </span>
                <span>•</span>
                <span>{company.industry}</span>
                <span>•</span>
                <span>{company.size}</span>
              </div>
              <p className="text-gray-700">{company.description}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* About Section */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">About</h2>
              <div className="space-y-3">
                <div>
                  <span className="font-semibold text-gray-700">Location:</span>
                  <span className="text-gray-600 ml-2">{company.location}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Founded:</span>
                  <span className="text-gray-600 ml-2">{company.founded}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Website:</span>
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline ml-2"
                  >
                    {company.website}
                  </a>
                </div>
              </div>
            </div>

            {/* Culture Section */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Culture</h2>
              <p className="text-gray-700">{company.culture}</p>
            </div>

            {/* Benefits Section */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Benefits</h2>
              <div className="grid grid-cols-2 gap-3">
                {company.benefits && company.benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="flex items-center bg-blue-50 px-4 py-2 rounded-lg"
                  >
                    <span className="text-blue-600 mr-2">✓</span>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Reviews Section */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Employee Reviews ({reviews.length})
              </h2>

              {reviews.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No reviews yet</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map(review => (
                    <div key={review.id} className="border-b border-gray-200 pb-4 last:border-0">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-semibold text-gray-800">
                            {review.reviewerName}
                          </span>
                          <span className="text-gray-500 text-sm ml-2">
                            {review.date}
                          </span>
                        </div>
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <span
                              key={i}
                              className={`text-lg ${
                                i < review.rating ? 'text-yellow-500' : 'text-gray-300'
                              }`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-700">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}

              <Link
                to={`/reviews/${companyId}`}
                className="mt-4 inline-block text-blue-600 hover:underline"
              >
                Write a Review
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Open Positions */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Open Positions
              </h3>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  {company.openPositions}
                </div>
                <p className="text-gray-600 text-sm mb-4">Available Positions</p>
                <button className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700">
                  View Jobs
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Industry</span>
                  <span className="font-semibold text-gray-800">{company.industry}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Company Size</span>
                  <span className="font-semibold text-gray-800">{company.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Founded</span>
                  <span className="font-semibold text-gray-800">{company.founded}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Rating</span>
                  <span className="font-semibold text-gray-800">{averageRating} / 5.0</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfilePage;
